/**
 * URL routing helpers.
 *
 * URL shape:
 *   /                              → no project, default view
 *   /<projectId>/                  → project selected, default view
 *   /<projectId>/#<view>           → project + view  (e.g. /projecta/#geomap)
 *   /<projectId>/#<view>_<extra>   → legacy `_<extra>` suffix kept
 */

export interface UrlState {
    view: string;
    projectId: string | null;
    extra: string | null;
}

const LOCATION_CHANGE_EVENT = 'locationchange';

function parsePathname(pathname: string): {projectId: string | null} {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] === 'project' && segments[1]) {
        try {
            return {projectId: decodeURIComponent(segments[1])};
        } catch {
            return {projectId: segments[1]};
        }
    }
    const first = segments[0];
    if (!first) return {projectId: null};
    try {
        return {projectId: decodeURIComponent(first)};
    } catch {
        return {projectId: first};
    }
}

function parseHashOnly(hash: string): {view: string, extra: string | null} {
    let raw = hash || '';
    if (raw.startsWith('#')) raw = raw.substring(1);
    if (raw.startsWith('/')) raw = raw.substring(1);
    let extra: string | null = null;
    const uIdx = raw.indexOf('_');
    if (uIdx !== -1) {
        extra = raw.substring(uIdx + 1);
        raw = raw.substring(0, uIdx);
    }
    return {view: raw, extra};
}

export function parseUrl(): UrlState {
    return {
        ...parsePathname(window.location.pathname),
        ...parseHashOnly(window.location.hash),
    };
}

function buildPathname(projectId: string | null): string {
    // Kein trailing slash, damit der Dev-Server das nicht als Verzeichnis-Request
    // interpretiert (sonst 404 statt SPA-Fallback auf index.html).
    return projectId ? '/' + encodeURIComponent(projectId) : '/';
}

function buildHash(view: string, extra?: string | null): string {
    if (!view) return '';
    let h = view;
    if (extra) h += '_' + extra;
    return '#' + h;
}

interface NavigateOpts {
    view?: string;
    projectId?: string | null;
    extra?: string | null;
}

/**
 * Navigate to a new URL. `projectId` undefined keeps the current project,
 * `projectId: null` removes it. Same for `view`. `extra` defaults to none.
 */
export function navigate(opts: NavigateOpts): void {
    const current = parseUrl();
    const projectId = opts.projectId !== undefined ? opts.projectId : current.projectId;
    const view = opts.view !== undefined ? opts.view : current.view;
    const extra = opts.extra !== undefined ? opts.extra : null;

    const newPath = buildPathname(projectId);
    const newHash = buildHash(view, extra);
    const newUrl = newPath + window.location.search + newHash;

    if (newPath !== window.location.pathname) {
        // Path change requires history API; pushState fires no event on its own
        // so we dispatch a custom event for our listener.
        window.history.pushState(null, '', newUrl);
        window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
    } else if (newHash !== window.location.hash) {
        // Hash-only change → browser fires hashchange automatically.
        window.location.hash = newHash;
    }
}

/**
 * Subscribe to any URL change (path or hash). Combines hashchange, popstate,
 * and our custom pushState event so consumers can listen once.
 */
export function onUrlChange(handler: () => void): () => void {
    window.addEventListener('hashchange', handler);
    window.addEventListener('popstate', handler);
    window.addEventListener(LOCATION_CHANGE_EVENT, handler);
    return () => {
        window.removeEventListener('hashchange', handler);
        window.removeEventListener('popstate', handler);
        window.removeEventListener(LOCATION_CHANGE_EVENT, handler);
    };
}

